//#region IMPORT
import React, { useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import dayjs from 'dayjs';
import { Box, CssBaseline } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useDispatch, useSelector } from 'react-redux';
import { Capacitor } from '@capacitor/core';
import { showSnackbar } from '../Reducer/SnackbarReducer';
import { WorkingCheckActions } from '../Reducer/WorkingCheckReducer';
import { LocalNotifications } from '@capacitor/local-notifications';
import { App } from '@capacitor/app';
import { BaseInfoActions } from '../Reducer/BaseInfoReducer';
import AppBarPermit from './../Components/Ui/AppBarPermit';
import Commons from '../Services/Common/Common';
import {
  useSearch1,
  useSearch2,
  useSearch3,
  useSearch4,
  useSearch5,
  useSearch6,
  useSearch7,
  useSave1,
} from './Layout_Query';
import { initFcm } from '../firebase';
import { HomeInfoActions } from '../Reducer/HomeInfoReducer';
import {
  setCustCd,
  setCustList,
  setDepList,
  clearResetFlag,
  setGongjongList,
} from '../Reducer/SearchReducer';
import { PermitActions } from '../Reducer/PermitReducer';
import { EquipmentActions } from '../Reducer/EquipmentReducer';
import { useFcmReceiver } from '../Hooks/useFcmListener';
import AppBar from './../Components/Ui/AppBar';
import { useBackHandler } from '../Hooks/back/useBackHandler';
import { useAppNavigate } from '../Hooks/back/useAppNavigate';
import { useSave } from '../Hooks/useSave';
//#endregion

const Main = styled('main', { shouldForwardProp: (prop) => prop !== 'open' })(
  ({ theme, open }) => ({
    flexGrow: 1,
    width: '100%',
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    height: 'calc(100vh - 56px)',
    marginTop: '56px',
    overflowY: 'auto',
    overflowX: 'hidden',
  })
);

const INTERNAL_MENU = [
  'M_TBMManager',
  'M_TBM',
  'M_Permit',
  'M_TRA',
  'M_TRA_SIGN',
  'M_EmergencyTel',
  'M_WorkStop/write',
  'M_WorkStop',
];

/** AppBar 하단 fixed 네비게이션(h-16) — appBarDefault는 Layout mb 미적용되어 콘텐츠가 가려짐 */
const BOTTOM_NAV_HEIGHT = 64;

export default function Layout() {
  //#region 변수

  const { statusBar, navBar } = useSelector((state) => state.systemBar);
  const { type, bgColor } = useSelector((s) => s.navBarInfo);
  const { resetSearchInfo } = useSelector((state) => state.searchInfo);
  const { userInfo, companyInfo } = useSelector((state) => state.BaseInfo);
  const { riskWorkerOpinionMenuActive } = useSelector(
    (state) => state.homeInfo
  );
  const { navigate } = useAppNavigate();
  const dispatch = useDispatch();
  const location = useLocation();

  // 앱 전체 뒤로가기 처리 (Capacitor 하드웨어 버튼 포함)
  const handleBack = useBackHandler();
  //#endregion

  useEffect(() => {
    const requestPermission = async () => {
      if (Capacitor.isNativePlatform()) {
        const perm = await LocalNotifications.requestPermissions();
        console.log('🔔 Local Notification Permission:', perm);
      }
    };
    requestPermission();
  }, []);

  useEffect(() => {
    SearchLogic1();
  }, []);

  // 라우터 주소 바뀔때마다 메뉴 최신화
  useEffect(() => {
    Search1();
    Search2();
    Search4();
    Search5();
    Search7();
  }, [location.pathname]);

  const { refetch: Search1 } = useSearch1({
    onSuccess: async (data) => {
      const userInfo = data?.userInfo
        ? {
            ...data.userInfo,
            decUserId: Commons.DecryptoAES(data.userInfo.userid),
          }
        : null;

      const lanInfo = data?.lanInfo ?? [];
      const companyInfo = data?.companyInfo ?? [];
      const custList = data?.custInfo.map((r) => ({
        ...r,
        name: r.custNm,
        value: r.custCd,
      }));
      const custInfo = custList ?? [];
      const defaultCustCd = custInfo[0].value;

      // 계약 기간: 일(날짜) 단위만 비교
      const today = dayjs().startOf('day');
      const contractStart = dayjs(
        data.companyInfo[0].contractStartDate
      ).startOf('day');
      const contractEnd = dayjs(data.companyInfo[0].contractEndDate).startOf(
        'day'
      );
      if (
        today.isBefore(contractStart, 'day') ||
        today.isAfter(contractEnd, 'day')
      ) {
        Save1();
      }

      let menuInfo = (data?.menuInfo ?? [])
        .filter((m) => String(m.screenType) === '1')
        .map((m) => {
          const menu = { ...m, open: false };

          if (['M_WorkEnd2'].includes(m.menuId)) {
            menu.requiresWorkCd = true;
          }

          if (['M_PPE', 'M_TBMManager', 'M_TBM'].includes(m.menuId)) {
            menu.hideWhenWorking = true;
          }

          return menu;
        });

      // 근로자 의견 메뉴 활성화 여부가 true면 M_RNM, false면 M_WorkInfo 추가
      menuInfo = menuInfo.filter((m) => {
        if (m.menuId === 'M_RNM') return riskWorkerOpinionMenuActive;
        if (m.menuId === 'M_WorkInfo') return !riskWorkerOpinionMenuActive;
        return true;
      });
      // 비상연락망(emtYn)에 따른 작업중지 메뉴 필터
      const isEmtUser =
        String(data.userInfo?.emtYn ?? '').toUpperCase() === 'Y';
      if (isEmtUser) {
        menuInfo = menuInfo.filter((m) => m.menuId !== 'M_WorkStop/write');
      } else {
        menuInfo = menuInfo.filter((m) => m.menuId !== 'M_WorkStop');
      }

      // 외부인인 경우 TBM 참석, 작업허가서, 교육일지 메뉴만 보여줌
      if (data.userInfo?.internalYn === '1') {
        menuInfo = menuInfo.filter((m) => INTERNAL_MENU.includes(m.menuId));
      }

      const menuInfoWithSetting = [
        ...menuInfo,
        {
          no: 0,
          authCd: '',
          authNm: '',
          deleteYn: 'N',
          proId: 'Home',
          menuNm: '홈',
          menuType: '0',
          menuUp: 'Home',
          printYn: 'N',
          saveYn: 'N',
          screenType: '0',
          seq: 0,
          open: false,
        },
        {
          no: 999999,
          authCd: '',
          authNm: '',
          deleteYn: 'N',
          grade: '',
          proId: 'Setting',
          menuId: 'Mypage',
          menuNm: '내정보',
          menuType: '0',
          menuUp: 'Setting',
          printYn: 'N',
          saveYn: 'N',
          screenType: '0',
          seq: 99999999999,
          open: false,
        },
      ];
      dispatch(BaseInfoActions.setUserInfo(userInfo));
      dispatch(BaseInfoActions.setMenuInfo(menuInfoWithSetting));
      dispatch(BaseInfoActions.setLanInfo(lanInfo));
      dispatch(BaseInfoActions.setCustInfo(custInfo));
      dispatch(BaseInfoActions.setCompanyInfo(companyInfo));
      dispatch(setCustList(custInfo));
      dispatch(setCustCd(defaultCustCd));
    },
  });

  const SearchLogic1 = async () => {
    try {
      const fcmResult = await initFcm();
      const firebaseToken = fcmResult?.token || '';
      Search1({ firebaseToken: firebaseToken });
    } catch (e) {
      console.error('❌ 초기 사용자 정보 조회 실패:', e);
    }
  };

  useEffect(() => {
    if (resetSearchInfo) {
      SearchLogic1();
      dispatch(clearResetFlag());
      Search3();
      Search6();
    }
  }, [resetSearchInfo, dispatch]);

  const { refetch: Search2 } = useSearch2({
    onSuccess: (rows) => {
      const data = rows[0];

      dispatch(
        HomeInfoActions.setNoticeData(Commons.InsertNumber(data.noticeInfo))
      );
      dispatch(
        HomeInfoActions.setTbmProgress(data.tbmProgressInfo[0].complete)
      );
      dispatch(
        HomeInfoActions.setTbmManagerYn(
          data.tbmManagerYnInfo[0].hasTbmConductedData
        )
      );
      dispatch(
        HomeInfoActions.setRiskWorkerOpinionMenuActive(
          data.riskWorkerOpinionMenuActive
        )
      );

      // 나의 결재 대기: 작업허가서(gubun=permit) / 작업재개(gubun=resume) 항목별 건수
      const waitingList = data.waitingPaymentInfo || [];
      let permitWaitCnt = 0;
      let permitLastTitle = '결재 대기 중인 문서가 없습니다.';
      let resumeWaitCnt = 0;
      let resumeLastTitle = '결재 대기 중인 문서가 없습니다.';
      waitingList.forEach((item) => {
        const cnt = item?.waitCnt ?? 0;
        const title = item?.lastTitle || '결재 대기 중인 문서가 없습니다.';
        const gubun = (item?.gubun ?? item?.type ?? '').toLowerCase();
        if (gubun === 'resume') {
          resumeWaitCnt = cnt;
          resumeLastTitle = title;
        } else {
          permitWaitCnt = cnt;
          permitLastTitle = title;
        }
      });
      if (
        waitingList.length > 0 &&
        !waitingList.some(
          (i) => (i?.gubun ?? i?.type ?? '').toLowerCase() === 'resume'
        )
      ) {
        const first = waitingList[0];
        permitWaitCnt = first?.waitCnt ?? 0;
        permitLastTitle = first?.lastTitle || permitLastTitle;
      }
      dispatch(HomeInfoActions.setPermitWaitCnt(permitWaitCnt));
      dispatch(HomeInfoActions.setPermitLastTitle(permitLastTitle));
      dispatch(HomeInfoActions.setResumeWaitCnt(resumeWaitCnt));
      dispatch(HomeInfoActions.setResumeLastTitle(resumeLastTitle));
      dispatch(
        HomeInfoActions.setWaitingPaymentInfoWaitCnt(
          permitWaitCnt + resumeWaitCnt
        )
      );
      dispatch(HomeInfoActions.setWaitingPaymentInfoLastTitle(permitLastTitle));
      dispatch(
        WorkingCheckActions.setWorkStopAlarm(resumeWaitCnt > 0 ? '요청' : '')
      );
      if (data.workStatusInfo && data.workStatusInfo.length > 0) {
        dispatch(WorkingCheckActions.setWorkCd(data.workStatusInfo[0].workCd));
      } else {
        dispatch(WorkingCheckActions.clearWorkEnd());
      }

      if (data.workStopInfo && data.workStopInfo.length > 0) {
        const { workStopYn, workStopCd } = data.workStopInfo[0];

        if (workStopYn === 'Y') {
          dispatch(WorkingCheckActions.setStopWorking(1));
          dispatch(WorkingCheckActions.setWorkStopCd(workStopCd));
        } else {
          dispatch(WorkingCheckActions.clearWorkStop());
        }
      } else {
        dispatch(WorkingCheckActions.clearWorkStop());
      }
    },
  });

  const { refetch: Search3 } = useSearch3({
    onSuccess: (rows) => {
      const depList = rows
        .filter((r) => r.depNmLast && r.depNmLast.trim() !== '')
        .map((r) => ({
          name: r.depNmLast,
          value: r.depCd,
        }));

      dispatch(setDepList(depList));
    },
  });

  const { refetch: Search4 } = useSearch4({
    onSuccess: (rows) => {
      const gubuns = rows.map((item) => item.gubun);

      if (gubuns.includes('반려')) {
        dispatch(PermitActions.setPermitAlarm('반려'));
      } else if (gubuns.includes('요청')) {
        dispatch(PermitActions.setPermitAlarm('요청'));
      } else {
        dispatch(PermitActions.setPermitAlarm(null));
      }
    },
  });

  const { run: Search5 } = useSearch5({
    onSuccess: (rows) => {
      const solveYn = rows[0]?.solveYn;

      if (solveYn === 'Y') {
        navigate('/Home');
        dispatch(WorkingCheckActions.setStopWorking(0));
      } else if (
        solveYn === 'N' &&
        rows.some((row) => row.stopId === userInfo?.decUserId)
      ) {
        if (userInfo?.authCd === 'EMPLOYEE') {
          navigate(`/M_WorkStop/${rows[0].workStopCd}`, {
            state: { workStopCd: rows[0].workStopCd },
          });
        }
      }
    },
  });
  useFcmReceiver(Search5);

  const { refetch: Search6 } = useSearch6({
    onSuccess: (rows) => {
      const gongjongList = rows.map((r) => ({
        name: r.gongjongNm,
        value: r.gongjongCd,
      }));
      dispatch(setGongjongList(gongjongList));
    },
  });

  const { refetch: Search7 } = useSearch7({
    onSuccess: (rows) => {
      const hasPendingReceipt = rows.some((equip) => equip.issuSign !== 'Y');
      dispatch(
        EquipmentActions.setEquipReceiptAlarm(
          hasPendingReceipt ? '수령하기' : ''
        )
      );
    },
  });

  const { runAsync: Save1Async } = useSave1({});

  // 저장
  const { run: Save1 } = useSave({
    menuId: 'Layout',
    requestSave: (rows) => Save1Async(rows),
    showSuccess: false,
    onSuccess: async (rows) => {
      const data = rows.saveDetailData[0];
      if (!data || (data?.Result !== 'Success' && data?.result !== 'Success')) {
        dispatch(
          showSnackbar({
            message: data?.message ?? '로그아웃 처리에 실패했습니다.',
            type: 'warning',
          })
        );
        return false;
      }
      Commons.Storages('set', 'auth', { auth: false });
      dispatch(
        showSnackbar({
          message: '계약 기간이 아닙니다. 계약 기간을 확인해주세요.',
          type: 'warning',
        })
      );
      navigate('/');
    },
  });
  // 네이티브 앱 상태 바뀔때 작업중지 여부 조회
  useEffect(() => {
    let listener = null;

    const CheckStopWorking = async () => {
      if (Capacitor.isNativePlatform()) {
        try {
          listener = await App.addListener('appStateChange', async (state) => {
            if (state.isActive) {
              Search2();
            }
          });
        } catch (e) {
          console.error(
            '❌ 네이티브 앱 상태 바뀔때 작업중지 여부 조회 실패:',
            e
          );
        }
      }
    };

    CheckStopWorking();

    return () => {
      if (listener) {
        listener.remove();
      }
    };
  }, []);
  // ✅ 상단 AppBar 설정 방법:
  // - type: "appBarDefault" 또는 "titleBar" 중 선택 가능
  //   - "appBarDefault": 기본 상단바 스타일
  //   - "titleBar": 사용자 지정 타이틀을 표시할 수 있는 상단바
  //     → 이 경우, titleName 속성으로 타이틀 텍스트 설정 필요
  // - bgColor: 전체 페이지의 배경색 지정에 사용됨
  const hasBottomNav = type === 'appBarDefault';
  const bottomNavReserve = hasBottomNav ? BOTTOM_NAV_HEIGHT : 0;

  return (
    <Box
      sx={{
        display: 'flex',
        backgroundColor: bgColor,
        flexDirection: 'column',
        minHeight: '100vh',
      }}
    >
      <CssBaseline />
      {type === 'permit' ? (
        <AppBarPermit handleBack={handleBack} />
      ) : (
        <AppBar handleBack={handleBack} />
      )}
      <Main
        sx={{
          mb: type === 'permit' || type === 'titleBar' ? `${navBar}px` : '0px',
          height: `calc(100vh - ${statusBar + navBar + 56 + bottomNavReserve}px)`,
          marginTop: `${statusBar + 56}px`,
        }}
      >
        <Outlet />
      </Main>
    </Box>
  );
}
